import { http } from "@/lib/api";
import {
  appendSubject,
  subjectBody,
  subjectParams,
  type IntakeSubject,
} from "@/features/intake/lib/intakeSubject";
import {
  isPreviewSubject,
  PREVIEW_RAD_ID,
  previewAttestation,
  previewCompleteOnboarding,
  previewDeleteFile,
  previewITOnboarding,
  previewReferences,
  previewReplaceFile,
  previewSaveInfoBatch,
  previewSaveITOnboarding,
  previewSaveReferences,
  previewSaveSpecialties,
  previewSetNotApplicable,
  previewSpecialties,
  previewStatus,
  previewSubmitAttestation,
  previewUpdateLegalName,
  previewUploadFile,
  previewUploadText,
} from "@/features/intake/lib/intakeSandbox";
import type { Radiologist } from "@/features/radiologists/types/radiologist";
import type {
  AttestationAnswer,
  AttestationOut,
  IntakeItem,
  IntakeRadiologist,
  IntakeStatus,
  ITOnboardingOut,
  LegalNameUpdate,
  ReferenceEntry,
  ReferencesOut,
  SpecialtiesOut,
} from "@/features/intake/types/intake";

/**
 * Every per-radiologist call below is addressed by an `IntakeSubject` rather than
 * a bare id, so it can reach a radiologist known only by their Drive folder as
 * well as one with a scheduler record (see `lib/intakeSubject.ts`).
 *
 * Each also routes to the in-memory sandbox instead of the network when it's
 * handed the preview subject — that's what lets a manager walk the onboarding
 * wizard without touching the database or Drive. See `lib/intakeSandbox.ts`.
 */

/** Build a query string for a subject plus any extra params. */
function subjectQuery(subject: IntakeSubject, extra: Record<string, string> = {}): string {
  return new URLSearchParams({ ...subjectParams(subject), ...extra }).toString();
}

/** Run a synchronous sandbox mutation with the same promise shape as an HTTP call. */
function sandboxed<T>(run: () => T): Promise<T> {
  try {
    return Promise.resolve(run());
  } catch (err) {
    return Promise.reject(err);
  }
}

/** Radiologists linked to the current account (drives Intake visibility + selector). */
export function getMyRadiologists(signal?: AbortSignal): Promise<Radiologist[]> {
  return http<Radiologist[]>("/api/v1/radiologists/me", {}, signal);
}

/**
 * Self-service: create + link the caller's own Radiologist record during intake.
 * Idempotent per account — returns the already-linked rad if one exists.
 */
export function createMyRadiologist(
  firstName: string,
  lastName: string,
  signal?: AbortSignal,
): Promise<Radiologist> {
  return http<Radiologist>(
    "/api/v1/radiologists/me",
    {
      method: "POST",
      body: JSON.stringify({ first_name: firstName, last_name: lastName }),
    },
    signal,
  );
}

/** Mark the caller-owned radiologist's intake walkthrough as finished. */
export function completeMyOnboarding(radId: number, signal?: AbortSignal): Promise<Radiologist> {
  // Onboarding is only ever completed by a rad against their own record, so this
  // one keeps a plain id rather than a subject.
  if (radId === PREVIEW_RAD_ID) return sandboxed(previewCompleteOnboarding);
  return http<Radiologist>(
    "/api/v1/radiologists/me/complete-onboarding",
    {
      method: "POST",
      body: JSON.stringify({ rad_id: radId }),
    },
    signal,
  );
}

/** Every radiologist the Intake page can act on, sorted by last then first name. */
export function getIntakeRadiologists(signal?: AbortSignal): Promise<IntakeRadiologist[]> {
  return http<IntakeRadiologist[]>("/api/v1/intake/radiologists", {}, signal);
}

/**
 * Change a radiologist's full legal name. Renames the Drive folder, updates the
 * scheduler record, and rewrites the checklist value — server-side, as one step.
 */
export function updateLegalName(
  subject: IntakeSubject,
  name: { firstName: string; middleName: string; lastName: string },
  signal?: AbortSignal,
): Promise<LegalNameUpdate> {
  if (isPreviewSubject(subject)) {
    return sandboxed(() =>
      previewUpdateLegalName(name.firstName, name.middleName, name.lastName),
    );
  }
  return http<LegalNameUpdate>(
    "/api/v1/intake/legal-name",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ...subjectBody(subject),
        first_name: name.firstName,
        middle_name: name.middleName,
        last_name: name.lastName,
      }),
    },
    signal,
  );
}

export function getIntakeStatus(
  subject: IntakeSubject,
  signal?: AbortSignal,
): Promise<IntakeStatus> {
  const fetchStatus = (s: IntakeSubject) =>
    http<IntakeStatus>(`/api/v1/intake/status?${subjectQuery(s)}`, {}, signal);
  return isPreviewSubject(subject) ? previewStatus(fetchStatus) : fetchStatus(subject);
}

export function uploadIntakeFile(
  subject: IntakeSubject,
  requiredKey: string,
  file: File,
  signal?: AbortSignal,
): Promise<IntakeItem> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewUploadFile(requiredKey, file.name));
  const form = new FormData();
  appendSubject(form, subject);
  form.append("required_key", requiredKey);
  form.append("file", file);
  return http<IntakeItem>("/api/v1/intake/upload", { method: "POST", body: form }, signal);
}

/** Save typed free text for a text-or-file document item (stored as a Google Doc). */
export function uploadIntakeText(
  subject: IntakeSubject,
  requiredKey: string,
  text: string,
  signal?: AbortSignal,
): Promise<IntakeItem> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewUploadText(requiredKey, text));
  return http<IntakeItem>(
    "/api/v1/intake/upload-text",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...subjectBody(subject), required_key: requiredKey, text }),
    },
    signal,
  );
}

export function replaceIntakeFile(
  subject: IntakeSubject,
  requiredKey: string,
  driveFileId: string,
  file: File,
  signal?: AbortSignal,
): Promise<IntakeItem> {
  if (isPreviewSubject(subject)) {
    return sandboxed(() => previewReplaceFile(requiredKey, driveFileId, file.name));
  }
  const form = new FormData();
  appendSubject(form, subject);
  form.append("required_key", requiredKey);
  form.append("drive_file_id", driveFileId);
  form.append("file", file);
  return http<IntakeItem>("/api/v1/intake/replace", { method: "POST", body: form }, signal);
}

export function deleteIntakeFile(
  subject: IntakeSubject,
  requiredKey: string,
  driveFileId: string,
  signal?: AbortSignal,
): Promise<IntakeItem> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewDeleteFile(requiredKey, driveFileId));
  const query = subjectQuery(subject, {
    required_key: requiredKey,
    drive_file_id: driveFileId,
  });
  return http<IntakeItem>(`/api/v1/intake/file?${query}`, { method: "DELETE" }, signal);
}

/**
 * Save the entire 'Other Information' form in a single request (one Save click).
 * `na` rides along per field (it writes to the same doc) — no separate endpoint.
 */
export function setIntakeInfoBatch(
  subject: IntakeSubject,
  fields: { required_key: string; value: string; na?: boolean }[],
  signal?: AbortSignal,
): Promise<IntakeItem[]> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewSaveInfoBatch(fields));
  return http<IntakeItem[]>(
    "/api/v1/intake/info/batch",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...subjectBody(subject), fields }),
    },
    signal,
  );
}

/** Mark (na=true) or clear (na=false) an optional item as Not Applicable. */
export function setIntakeNotApplicable(
  subject: IntakeSubject,
  requiredKey: string,
  na: boolean,
  signal?: AbortSignal,
): Promise<IntakeItem> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewSetNotApplicable(requiredKey, na));
  const form = new FormData();
  appendSubject(form, subject);
  form.append("required_key", requiredKey);
  form.append("na", String(na));
  return http<IntakeItem>("/api/v1/intake/na", { method: "POST", body: form }, signal);
}

export function getITOnboarding(subject: IntakeSubject, signal?: AbortSignal): Promise<ITOnboardingOut> {
  const fetchIT = (s: IntakeSubject) =>
    http<ITOnboardingOut>(`/api/v1/intake/it?${subjectQuery(s)}`, {}, signal);
  return isPreviewSubject(subject) ? previewITOnboarding(fetchIT) : fetchIT(subject);
}

export function saveITOnboarding(
  subject: IntakeSubject,
  selectedId: string | null,
  signal?: AbortSignal,
): Promise<ITOnboardingOut> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewSaveITOnboarding(selectedId));
  return http<ITOnboardingOut>(
    "/api/v1/intake/it",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...subjectBody(subject), selected_id: selectedId }),
    },
    signal,
  );
}

export function getSpecialties(subject: IntakeSubject, signal?: AbortSignal): Promise<SpecialtiesOut> {
  const fetchSpecialties = (s: IntakeSubject) =>
    http<SpecialtiesOut>(`/api/v1/intake/specialties?${subjectQuery(s)}`, {}, signal);
  return isPreviewSubject(subject) ? previewSpecialties(fetchSpecialties) : fetchSpecialties(subject);
}

export function saveSpecialties(
  subject: IntakeSubject,
  selectedIds: string[],
  signal?: AbortSignal,
): Promise<SpecialtiesOut> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewSaveSpecialties(selectedIds));
  return http<SpecialtiesOut>(
    "/api/v1/intake/specialties",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...subjectBody(subject), selected_ids: selectedIds }),
    },
    signal,
  );
}

export function getReferences(subject: IntakeSubject, signal?: AbortSignal): Promise<ReferencesOut> {
  const fetchReferences = (s: IntakeSubject) =>
    http<ReferencesOut>(`/api/v1/intake/references?${subjectQuery(s)}`, {}, signal);
  return isPreviewSubject(subject) ? previewReferences(fetchReferences) : fetchReferences(subject);
}

export function saveReferences(
  subject: IntakeSubject,
  references: ReferenceEntry[],
  signal?: AbortSignal,
): Promise<ReferencesOut> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewSaveReferences(references));
  return http<ReferencesOut>(
    "/api/v1/intake/references",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...subjectBody(subject), references }),
    },
    signal,
  );
}

export function getAttestation(subject: IntakeSubject, signal?: AbortSignal): Promise<AttestationOut> {
  const fetchAttestation = (s: IntakeSubject) =>
    http<AttestationOut>(`/api/v1/intake/attestation?${subjectQuery(s)}`, {}, signal);
  return isPreviewSubject(subject) ? previewAttestation(fetchAttestation) : fetchAttestation(subject);
}

export function submitAttestation(
  subject: IntakeSubject,
  answers: AttestationAnswer[],
  signal?: AbortSignal,
): Promise<{ filename: string }> {
  if (isPreviewSubject(subject)) return sandboxed(() => previewSubmitAttestation(answers));
  return http<{ filename: string }>(
    "/api/v1/intake/attestation",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...subjectBody(subject), answers }),
    },
    signal,
  );
}
